'use client';

import {
  ApiActivity,
  ApiActivityType,
  ApiBaseData,
  ApiItemType,
  ApiNote,
  ApiNoteType,
  ApiPaymentOption,
  ApiPreparePaymentRequestFromJSON,
  ApiTraveler,
  ApiTravelerToJSON
} from '@ibe/api';
import {
  Error,
  FormRef,
  ImageProgressbar,
  MEDIAQUERY_DEFAULTS,
  Overlay,
  OverlayContainer,
  ScrollerService,
  Success,
  useAuth,
  useConfig,
  useMediaQuery
} from '@ibe/components';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState, JSX } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { Element as ScrollTo } from 'react-scroll/modules';
import { CheckoutStepComponentProps } from '@/Models/CheckoutStep';
import KeysB2B from '@/Translations/generated/en/Checkout-B2B.json.keys';
import Keys from '@/Translations/generated/en/Checkout.json.keys';
import BackButton from './BackButton';
import { FormItemsProps } from '@/Config/travellerFormConfig';
import { useMount } from 'react-use';
import TravelerForms from './TravelerForms';
import { useWorkflowService } from '@/Util/contexts';
import { buildTravellerValidationSchema, getAge } from '@/components/checkout/externals';
import CheckoutPriceInfo from '@/components/checkout/CheckoutPriceInfo';
import { getPaymentStatus } from '@/Util/getPaymentStatus';
import TACSection from '@/components/checkout/TACSection';
import BookingUtils from '@/Util/BookingUtils';
import { configService } from '@/Config/config';
import PreferencesSection, {
  PreferenceSectionValues
} from '@/components/checkout/PreferencesSection';
import { ValidationError } from 'yup';
import { logger } from '@/Util/globals';
import { useTranslation } from '@/app/i18n/client';
import { useApi } from '@/Util/useApi';

export const scrollElementErrorTravelerBase = 'travelerErrorScroller';
import CheckoutStore from './CheckoutStore';
const scrollElementError = 'travellerErrorScroller';

const preferencesTranslations = new Map<string, Map<string, string>>([
  [
    'en',
    new Map<string, string>([
      ['firstPreference', ' Preference for double bed'],
      ['secondPreference', 'Preference for 2 single beds'],
      ['thirdPreference', 'Quiet room if possible'],
      ['fourthPreference', 'Preference for floor as high as possible'],
      ['fifthPreference', 'Preference for ground floor']
    ])
  ],
  [
    'fr',
    new Map<string, string>([
      ['firstPreference', 'Préférence pour le lit double'],
      ['secondPreference', 'Préférence pour 2 lits simples'],
      ['thirdPreference', 'Si possible chambre au calme'],
      ['fourthPreference', 'Préférence pour un étage élévé'],
      ['fifthPreference', 'Préférence pour une chambre en rez-de-chaussée']
    ])
  ],
  [
    'nl',
    new Map<string, string>([
      ['firstPreference', 'Voorkeur voor tweepersoonsbed'],
      ['secondPreference', 'Voorkeur voor 2 éénpersoonsbedden'],
      ['thirdPreference', 'Indien mogelijk rustige kamer'],
      ['fourthPreference', 'Voorkeur voor een zo hoog mogelijke verdieping'],
      ['fifthPreference', 'Voorkeur voor gelijkvloers']
    ])
  ]
]);

const Traveler = observer(function Traveler(props: CheckoutStepComponentProps) {
  const config = useConfig();
  const { store } = props;
  const booking = store.booking;
  const auth = useAuth();
  const api = useApi(true);
  const isDesktopView = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.lg });
  const isUserLoggedIn = auth.authState.isLoggedIn;
  const { t } = useTranslation(isUserLoggedIn ? 'Checkout-B2B' : 'Checkout');

  const { workflow, setNextStepSlug, setPaymentResponse } = useWorkflowService();
  const [travelers, setTravelers] = useState<ApiTraveler[]>(booking?.travelers || []);
  const [externalData, setExternalData] = useState<{
    COUNTRIES: ApiBaseData[];
    SALUTATIONS: ApiBaseData[];
  }>({ COUNTRIES: [], SALUTATIONS: [] });

  const formRefs = useRef<Array<FormRef<Record<string, unknown>>>>([]);

  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activityError, setActivityError] = useState<boolean | undefined>(undefined);
  const [activitySuccess, setActivitySuccess] = useState<boolean | undefined>(undefined);

  const [selectedPreferencesObj, setSelectedPreferencesObj] = useState<PreferenceSectionValues>({
    firstPreference: false,
    secondPreference: false,
    thirdPreference: false,
    fourthPreference: false,
    fifthPreference: false
  });

  function updateSelectedPreferencesObj(key: string): void {
    setSelectedPreferencesObj({
      ...selectedPreferencesObj,
      [key]: !selectedPreferencesObj[key as keyof PreferenceSectionValues]
    });
  }

  const [isTACFirstBoxChecked, setIsTACFirstBoxChecked] = useState<boolean>(false);
  const [isTACSecondBoxChecked, setIsTACSecondBoxChecked] = useState<boolean>(false);

  const [showFirstSlotError, setShowFirstSlotError] = useState<boolean>(false);
  const [showSecondSlotError, setShowSecondSlotError] = useState<boolean>(false);

  const scrollToError = (errorId: string): void => {
    ScrollerService.scrollTo(errorId, { offset: -100 });
  };

  useEffect((): void => {
    setTravelers(store.booking?.travelers || []);
  }, [booking]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useMount(async () => {
    await store.loadConfigurationData();
    await store.loadCountries();
    setExternalData({
      COUNTRIES: store.countries || [],
      SALUTATIONS: store.titles || []
    });
  });

  function getPreferencesNoteText(keysOfSelectedPreferences: string[]): string {
    let preferencesNoteText = '';

    [...preferencesTranslations.keys()].forEach(language => {
      preferencesNoteText += language + ' - ';

      keysOfSelectedPreferences.forEach(keyOfSelectedPreferences => {
        const preferenceText = preferencesTranslations.get(language)?.get(keyOfSelectedPreferences);

        preferencesNoteText += preferenceText + ', ';
      });

      preferencesNoteText += '\n';
    });
    return preferencesNoteText;
  }

  const onSubmit = async (): Promise<void> => {
    validateTAC();

    try {
      setIsLoading(true);
      const formResults = (
        await Promise.all(formRefs.current.map(ref => ref?.triggerSubmit()))
      ).filter(item => !!item);

      if (formResults.some(result => Object.keys(result).length === 0)) {
        const index = formResults.findIndex(result => Object.keys(result).length === 0);
        scrollToError(scrollElementErrorTravelerBase + index);
        return;
      }

      if (
        showFirstSlotError ||
        showSecondSlotError ||
        !isTACFirstBoxChecked ||
        !isTACSecondBoxChecked
      ) {
        return;
      }

      const AllTravelers: ApiTraveler[] = formResults.map(item => {
        const result = item as FormItemsProps;
        const travelerInfoInBooking = travelers.find(
          (traveler: ApiTraveler) => traveler.id === result.id
        );
        if (travelerInfoInBooking) {
          return ApiTravelerToJSON({
            ...travelerInfoInBooking,
            birthDate: result.birthDate,
            age: getAge(result.birthDate),
            salutation: result.salutation,
            communicationDetails: {
              phone: result.phone,
              email: result.email
            },
            firstName: result.firstname,
            lastName: result.lastname,
            address: {
              city: result.city,
              countryCode: result.country,
              postalCode: result.zipCode,
              street: result.street
            }
          });
        }
      });

      await store.updateTravelers(AllTravelers);

      if (auth.authService.isLoggedIn) {
        await addPreferencesToBooking();

        let url;
        if (store?.booking?.id) {
          url = await api.preparePayment(
            store.booking.id,
            ApiPreparePaymentRequestFromJSON({
              isDeposit: false,
              isQuotationBooking: false,
              paymentMethod: ApiPaymentOption.INVOICE
            })
          );
        }
        if (url) {
          setPaymentResponse(getPaymentStatus(url));
        }
        setNextStepSlug(workflow.nextStepSlug || '');
      } else if (store?.booking?.id) {
        const activity: ApiActivity = {
          bookingId: store.booking.id,
          type: ApiActivityType.BOOKINGRESERVATION
        };
        await api
          .createActivity(activity)
          .then(() => setActivitySuccess(true))
          .catch(() => setActivityError(true));
      }
    } catch (e) {
      logger('error')(t(Keys.SummaryBookingError), e);
      setError(t(Keys.SummaryBookingError));
      scrollToError(scrollElementError);
    } finally {
      setIsLoading(false);
    }
  };

  const addPreferencesToBooking = async (): Promise<void> => {
    const keysOfSelectedPreferences: string[] = [];

    Object.keys(selectedPreferencesObj).forEach(function (key) {
      if (selectedPreferencesObj[key as keyof PreferenceSectionValues]) {
        keysOfSelectedPreferences.push(key);
      }
    });

    if (keysOfSelectedPreferences.length) {
      const hotelServiceId = store.booking?.bookedItems.filter(
        item => item.itemType === ApiItemType.HOTEL
      )[0].id;

      if (hotelServiceId) {
        await store.updateServiceNotes(
          {
            serviceId: hotelServiceId,
            notes: Array.of<ApiNote>({
              id: '',
              type: ApiNoteType.SPECIALREQUEST,
              text: getPreferencesNoteText(keysOfSelectedPreferences)
            }),
            bookingNumber: store.booking?.id || ''
          },
          false
        );
      }
    }
  };

  const createQuoteBooking = async (): Promise<void> => {
    if (!store.booking) {
      setError(t(Keys.SummaryBookingError));
      return;
    }

    try {
      setIsLoading(true);
      const formResults = (
        await Promise.all(formRefs.current.map(ref => ref?.triggerSubmitWithoutValidation()))
      ).filter(item => !!item);
      let quoteBookingTravelers: ApiTraveler[];

      await Promise.all(
        travelers.map(async (traveler, index) => {
          const travelerFormResultsByID = formResults[index];
          try {
            await buildTravellerValidationSchema(config, t, traveler.type).validate(
              travelerFormResultsByID,
              { abortEarly: false }
            );
          } catch (err: any) {
            err.inner.forEach((error: ValidationError) => {
              if (error.path) {
                formResults[index][error.path] = undefined;
              }
            });
          }
        })
      );

      quoteBookingTravelers = BookingUtils.mapTravelersForQuoteBooking(
        store.booking,
        formResults as Record<string, ApiTraveler>[]
      );

      await store.updateTravelers(quoteBookingTravelers);

      if (auth.authService.isLoggedIn) {
        let url;
        if (store?.booking?.id) {
          url = await api.preparePayment(
            store.booking.id,
            ApiPreparePaymentRequestFromJSON({
              isDeposit: false,
              isQuotationBooking: true,
              paymentMethod: ApiPaymentOption.INVOICE
            })
          );
        }
        if (url) {
          setPaymentResponse(getPaymentStatus(url));
        }
        setNextStepSlug(workflow.nextStepSlug || '');
      }
    } catch (e) {
      logger('error')(t(Keys.SummaryBookingError), e);
      setError(t(Keys.SummaryBookingError));
      scrollToError(scrollElementError);
    } finally {
      setIsLoading(false);
    }
    return Promise.resolve();
  };

  function validateTAC(): void {
    if (!isTACFirstBoxChecked) {
      setShowFirstSlotError(true);
    }

    if (!isTACSecondBoxChecked) {
      setShowSecondSlotError(true);
    }
  }

  return (
    <>
      <OverlayContainer className="checkout__container__wrapper">
        {!activityError && !activitySuccess && (
          <>
            <div className="checkout__container__content">
              <ProgressbarWrapper isDesktopView={isDesktopView} store={store} />
              <ErrorWrapper error={error} />
              {isUserLoggedIn && !configService.get().hideCreateQuotationBookingButton && (
                <div className="d-flex justify-content-end p-2">
                  <Button
                    className="createQuoteBookingButton"
                    color="primary"
                    onClick={() => createQuoteBooking()}
                  >
                    {t(KeysB2B.createQuoteBookingButtonLabel)}
                  </Button>
                </div>
              )}
              {store.booking?.travelers && store.booking.bookedItems?.length > 0 && (
                <div>
                  <div className="travelerForms_container">
                    <TravelerForms
                      travelers={travelers}
                      setTravelers={setTravelers}
                      formRefs={formRefs}
                      booking={store.booking}
                      externalData={externalData}
                    />
                  </div>
                  <PreferencesSection
                    onCheckboxChange={updateSelectedPreferencesObj}
                    values={selectedPreferencesObj}
                  />
                </div>
              )}
              <TACSection
                isFirstSlotChecked={isTACFirstBoxChecked}
                isSecondSlotChecked={isTACSecondBoxChecked}
                onFirstSlotCheckboxChange={() => {
                  setIsTACFirstBoxChecked(!isTACFirstBoxChecked);
                  setShowFirstSlotError(isTACFirstBoxChecked);
                }}
                onSecondSlotCheckboxChange={() => {
                  setIsTACSecondBoxChecked(!isTACSecondBoxChecked);
                  setShowSecondSlotError(isTACSecondBoxChecked);
                }}
                showFirstSlotError={showFirstSlotError}
                showSecondSlotError={showSecondSlotError}
              />

              {isDesktopView && <Nav onSubmit={onSubmit} />}
            </div>
            <CheckoutPriceInfo
              store={store}
              next={onSubmit}
              backButton={<BackButton className={'w-100'} />}
            />
            {isLoading && <Overlay />}
          </>
        )}
      </OverlayContainer>
      {activitySuccess && <Success message={t(Keys.activityCreatedSuccessfully)} />}
      {activityError && <Error message={t(Keys.activityCreatedError)} />}
    </>
  );
});

const Nav = ({ onSubmit }: { onSubmit: () => void }): JSX.Element => {
  const auth = useAuth();
  const isUserLoggedIn = auth.authState.isLoggedIn;
  const { t } = useTranslation(isUserLoggedIn ? 'Checkout-B2B' : 'Checkout');

  return (
    <Row className="d-sm-flex mt-4">
      <Col className="col-sm-4 col-12">
        <BackButton />
      </Col>
      <Col className="col-sm-8 col-12 d-flex justify-content-end">
        <Button
          className="navButton"
          color="primary"
          onClick={(): void => {
            onSubmit();
          }}
        >
          {auth.authService.isLoggedIn ? t(Keys.order) : t(Keys.sendRequest)}
        </Button>
      </Col>
    </Row>
  );
};

const ProgressbarWrapper = observer(
  ({ store, isDesktopView }: { store: CheckoutStore; isDesktopView: boolean }) => {
    const auth = useAuth();
    const { workflow, setNextStepSlug } = useWorkflowService();
    const isUserLoggedIn = auth.authState.isLoggedIn;
    const { t } = useTranslation(isUserLoggedIn ? 'Checkout-B2B' : 'Checkout');

    const translatedSteps = store.steps.map(step => {
      return { ...step, description: t(step.description) };
    });
    return (
      <ImageProgressbar
        steps={translatedSteps}
        onClick={(index: number): void => {
          setNextStepSlug(store.handleNavigationClick(workflow, index - 1));
        }}
        showNotActiveIcon
        showDescriptionOnlyForActiveTab={!isDesktopView}
      />
    );
  }
);

const ErrorWrapper = observer(({ error }: { error?: string }) => (
  <ScrollTo name={scrollElementError}>{error && <Error message={error || ''} />}</ScrollTo>
));

export default Traveler;
